/**
 * The twelve traditional African instruments featured on the soundboard.
 *
 * Names, origins and descriptions are transcribed from the source
 * spreadsheet (非洲传统乐器清单). Sounds are FluidR3_GM soundfont samples
 * chosen to approximate each instrument's character — they stand in for
 * real field recordings, which the pads are structured to accept later.
 */

const AUDIO = '/audio/african';
const IMAGES = '/images/african';

export interface InstrumentPad {
  /** Stroke or note name, e.g. "Slap" or "C4" */
  label: string;
  /** Chinese label shown under the English one */
  labelZh: string;
  /** Sample URL, relative to the public root */
  src: string;
  /**
   * Playback-rate multiplier. Used where a stroke is a bent or squeezed
   * version of the same sample rather than a separate recording —
   * 2 is an octave up, 0.5 an octave down.
   */
  pitchRatio?: number;
  /** Keyboard key that fires this pad */
  keyBinding: string;
}

export interface AfricanInstrument {
  slug: string;
  nameZh: string;
  nameEn: string;
  /** Origin region / ethnic group */
  originZh: string;
  originEn: string;
  /** The 核心特点 column, verbatim */
  descriptionZh: string;
  descriptionEn: string;
  image: string;
  pads: InstrumentPad[];
}

export const AFRICAN_INSTRUMENTS: AfricanInstrument[] = [
  {
    slug: 'djembe',
    nameZh: '金贝鼓',
    nameEn: 'Djembe',
    originZh: '西非曼丁文化（马里、几内亚）',
    originEn: 'Mandinka culture, West Africa (Mali, Guinea)',
    descriptionZh:
      '整木掏空成高脚杯形，蒙山羊皮，绳编调音；徒手演奏可发出低中高三音，是曼丁鼓乐的核心',
    descriptionEn:
      'A goblet carved from a single log, headed with goatskin and rope-tuned. Played bare-handed, it speaks in three voices — bass, tone and slap — and anchors Mandinka drum music.',
    image: `${IMAGES}/djembe.jpg`,
    pads: [
      { label: 'Bass', labelZh: '低音', src: `${AUDIO}/taiko_drum-A1.mp3`, keyBinding: 'q' },
      { label: 'Tone', labelZh: '中音', src: `${AUDIO}/taiko_drum-E2.mp3`, keyBinding: 'w' },
      { label: 'Slap', labelZh: '高音', src: `${AUDIO}/taiko_drum-A2.mp3`, keyBinding: 'e' },
    ],
  },
  {
    slug: 'dunun',
    nameZh: '墩墩鼓',
    nameEn: 'Dunun',
    originZh: '西非曼丁文化',
    originEn: 'Mandinka culture, West Africa',
    descriptionZh:
      '圆柱形双面牛皮鼓，一套3个（高/中/低音），鼓槌演奏，为鼓乐提供低音根基，常与金贝鼓合奏',
    descriptionEn:
      'Cylindrical double-headed cowskin drums played with sticks, in a set of three — high, middle and low. They lay the bass foundation beneath the djembe.',
    image: `${IMAGES}/dunun.jpg`,
    pads: [
      { label: 'Kenkeni', labelZh: '高音', src: `${AUDIO}/melodic_tom-C3.mp3`, keyBinding: 'r' },
      { label: 'Sangban', labelZh: '中音', src: `${AUDIO}/melodic_tom-A2.mp3`, keyBinding: 't' },
      { label: 'Doundounba', labelZh: '低音', src: `${AUDIO}/melodic_tom-C2.mp3`, keyBinding: 'y' },
    ],
  },
  {
    slug: 'talking-drum',
    nameZh: '说话鼓',
    nameEn: 'Talking Drum / Tama',
    originZh: '西非（加纳、尼日利亚、塞内加尔）',
    originEn: 'West Africa (Ghana, Nigeria, Senegal)',
    descriptionZh:
      '沙漏形双面鼓，夹在腋下挤压皮绳可变音高，能模仿人说话语调，历史上用于跨部落传讯',
    descriptionEn:
      'An hourglass drum held under the arm; squeezing its cords bends the pitch, letting it mimic the contours of speech. Historically used to send messages between villages.',
    image: `${IMAGES}/talking-drum.jpg`,
    pads: [
      { label: 'Low', labelZh: '低音', src: `${AUDIO}/melodic_tom-C3.mp3`, keyBinding: 'u' },
      { label: 'High', labelZh: '高音', src: `${AUDIO}/melodic_tom-G3.mp3`, keyBinding: 'i' },
      {
        label: 'Bend',
        labelZh: '滑音',
        src: `${AUDIO}/melodic_tom-G3.mp3`,
        pitchRatio: 1.35,
        keyBinding: 'o',
      },
    ],
  },
  {
    slug: 'bata',
    nameZh: '巴塔鼓',
    nameEn: 'Bata',
    originZh: '尼日利亚约鲁巴族',
    originEn: 'Yoruba people, Nigeria',
    descriptionZh: '一套3个双面锥形鼓，两鼓面音高不同，是约鲁巴族宗教祭祀专用圣鼓',
    descriptionEn:
      'A set of three double-headed conical drums, each head tuned differently. Sacred drums reserved for Yoruba religious ceremony.',
    image: `${IMAGES}/bata.jpg`,
    pads: [
      { label: 'Okónkolo', labelZh: '小鼓', src: `${AUDIO}/melodic_tom-C3.mp3`, keyBinding: 'a' },
      { label: 'Itótele', labelZh: '中鼓', src: `${AUDIO}/melodic_tom-G2.mp3`, keyBinding: 's' },
      { label: 'Iyá', labelZh: '母鼓', src: `${AUDIO}/melodic_tom-C2.mp3`, keyBinding: 'd' },
    ],
  },
  {
    slug: 'sabar',
    nameZh: '萨巴鼓',
    nameEn: 'Sabar',
    originZh: '塞内加尔沃洛夫族',
    originEn: 'Wolof people, Senegal',
    descriptionZh:
      '鼓身修长，一手徒手、一手持棍演奏，是塞内加尔Mbalax音乐核心，节奏跳跃，用于舞蹈伴奏',
    descriptionEn:
      'A tall, slender drum played with one bare hand and one stick. The heart of Senegalese Mbalax, its leaping rhythms drive the dance.',
    image: `${IMAGES}/sabar.jpg`,
    pads: [
      { label: 'Open', labelZh: '手击', src: `${AUDIO}/taiko_drum-C3.mp3`, keyBinding: 'f' },
      { label: 'Stick', labelZh: '棍击', src: `${AUDIO}/taiko_drum-G3.mp3`, keyBinding: 'g' },
    ],
  },
  {
    slug: 'bougarabou',
    nameZh: '布加拉布鼓',
    nameEn: 'Bougarabou',
    originZh: '西非科特迪瓦、几内亚乔拉族',
    originEn: 'Jola people, Côte d’Ivoire and Guinea',
    descriptionZh: '比金贝鼓更长的杯形手鼓，可同时演奏多面，低音浑厚悠长，独奏氛围感强',
    descriptionEn:
      'A goblet hand drum longer than the djembe, often played several at once. Its bass is round and long-sustaining, well suited to solo playing.',
    image: `${IMAGES}/bougarabou.jpg`,
    pads: [
      { label: 'Bass', labelZh: '低音', src: `${AUDIO}/taiko_drum-C2.mp3`, keyBinding: 'h' },
      { label: 'Tone', labelZh: '中音', src: `${AUDIO}/taiko_drum-G2.mp3`, keyBinding: 'j' },
    ],
  },
  {
    slug: 'ashiko',
    nameZh: '阿希科鼓',
    nameEn: 'Ashiko',
    originZh: '西非尼日利亚、贝宁约鲁巴族',
    originEn: 'Yoruba people, Nigeria and Benin',
    descriptionZh: '直锥形手鼓，底部开口通透，音色明亮清脆，用于部落集会、丰收庆典伴奏',
    descriptionEn:
      'A straight-sided conical hand drum, open at the base. Bright and crisp, it accompanies village gatherings and harvest celebrations.',
    image: `${IMAGES}/ashiko.jpg`,
    pads: [
      { label: 'Tone', labelZh: '中音', src: `${AUDIO}/melodic_tom-E3.mp3`, keyBinding: 'k' },
      { label: 'Slap', labelZh: '高音', src: `${AUDIO}/melodic_tom-C4.mp3`, keyBinding: 'l' },
    ],
  },
  {
    slug: 'kpanlogo',
    nameZh: '克潘洛戈鼓',
    nameEn: 'Kpanlogo',
    originZh: '加纳加族',
    originEn: 'Ga people, Ghana',
    descriptionZh:
      '桶形手鼓，木楔调音，鼓身常雕刻非洲传统图腾，节奏轻快，是加纳青年社交舞蹈伴奏乐器',
    descriptionEn:
      'A barrel-shaped hand drum tuned with wooden pegs, its shell often carved with traditional motifs. Its light, quick rhythms accompany Ghanaian social dance.',
    image: `${IMAGES}/kpanlogo.jpg`,
    pads: [
      { label: 'Tone', labelZh: '中音', src: `${AUDIO}/melodic_tom-D3.mp3`, keyBinding: 'z' },
      { label: 'Slap', labelZh: '高音', src: `${AUDIO}/melodic_tom-A3.mp3`, keyBinding: 'x' },
    ],
  },
  {
    slug: 'ngoma',
    nameZh: '恩戈马鼓',
    nameEn: 'Ngoma',
    originZh: '东非/南非班图族（坦桑尼亚、祖鲁族）',
    originEn: 'Bantu peoples, East and Southern Africa (Tanzania, Zulu)',
    descriptionZh:
      '大型筒形牛皮鼓，鼓身厚重，是东非/南非代表性仪式鼓，用于庆典、成人礼等重要场合',
    descriptionEn:
      'A large, heavy cylindrical cowskin drum — the ceremonial drum of East and Southern Africa, sounded at celebrations and rites of passage.',
    image: `${IMAGES}/ngoma.jpg`,
    pads: [
      { label: 'Deep', labelZh: '低音', src: `${AUDIO}/taiko_drum-A1.mp3`, keyBinding: 'c' },
      { label: 'Open', labelZh: '开放音', src: `${AUDIO}/taiko_drum-E2.mp3`, keyBinding: 'v' },
    ],
  },
  {
    slug: 'slit-drum',
    nameZh: '非洲缝鼓（木鼓）',
    nameEn: 'Slit Drum / Log Drum',
    originZh: '撒哈拉以南非洲广泛流传',
    originEn: 'Widespread across sub-Saharan Africa',
    descriptionZh:
      '整木掏空开长缝，敲击不同壁厚发不同音高，历史上作通讯鼓，声音可传数公里，音色低沉悠远',
    descriptionEn:
      'A hollowed log cut with a long slit; striking walls of differing thickness yields different pitches. Once a signalling drum, its low tone carried for kilometres.',
    image: `${IMAGES}/slit-drum.jpg`,
    pads: [
      { label: 'Low', labelZh: '低音', src: `${AUDIO}/woodblock-C3.mp3`, keyBinding: 'b' },
      { label: 'High', labelZh: '高音', src: `${AUDIO}/woodblock-G3.mp3`, keyBinding: 'n' },
    ],
  },
  {
    slug: 'balafon',
    nameZh: '巴拉风（非洲木琴）',
    nameEn: 'Balafon',
    originZh: '西非曼丁文化',
    originEn: 'Mandinka culture, West Africa',
    descriptionZh: '硬木琴键配葫芦共鸣器，橡胶头木槌敲击，音色清脆温暖，传统用于伴奏曼丁史诗讲述',
    descriptionEn:
      'Hardwood keys over gourd resonators, struck with rubber-headed mallets. Warm and ringing, it traditionally accompanies the telling of Mandinka epics.',
    image: `${IMAGES}/balafon.jpg`,
    pads: [
      { label: 'C4', labelZh: '宫', src: `${AUDIO}/marimba-C4.mp3`, keyBinding: '1' },
      { label: 'E4', labelZh: '角', src: `${AUDIO}/marimba-E4.mp3`, keyBinding: '2' },
      { label: 'G4', labelZh: '徵', src: `${AUDIO}/marimba-G4.mp3`, keyBinding: '3' },
      { label: 'C5', labelZh: '高宫', src: `${AUDIO}/marimba-C5.mp3`, keyBinding: '4' },
    ],
  },
  {
    slug: 'mbira',
    nameZh: '姆比拉（拇指钢琴）',
    nameEn: 'Mbira',
    originZh: '津巴布韦绍纳族',
    originEn: 'Shona people, Zimbabwe',
    descriptionZh: '木板固定金属簧片，拇指拨奏，体积小巧，音色空灵清澈，传统用于宗教仪式和冥想',
    descriptionEn:
      'Metal tines fixed to a wooden board and plucked with the thumbs. Small, clear and ethereal, it is played in religious ceremony and meditation.',
    image: `${IMAGES}/mbira.jpg`,
    pads: [
      { label: 'C4', labelZh: '宫', src: `${AUDIO}/kalimba-C4.mp3`, keyBinding: '5' },
      { label: 'E4', labelZh: '角', src: `${AUDIO}/kalimba-E4.mp3`, keyBinding: '6' },
      { label: 'G4', labelZh: '徵', src: `${AUDIO}/kalimba-G4.mp3`, keyBinding: '7' },
      { label: 'C5', labelZh: '高宫', src: `${AUDIO}/kalimba-C5.mp3`, keyBinding: '8' },
    ],
  },
];

/** Every distinct sample the board references, for preloading. */
export const ALL_SAMPLE_URLS = Array.from(
  new Set(AFRICAN_INSTRUMENTS.flatMap((i) => i.pads.map((p) => p.src))),
);

/** Keyboard key → pad lookup, built once for the keydown handler. */
export const PAD_BY_KEY = new Map(
  AFRICAN_INSTRUMENTS.flatMap((instrument) =>
    instrument.pads.map(
      (pad) => [pad.keyBinding, { instrument: instrument.slug, pad }] as const,
    ),
  ),
);
